import React, { useCallback, useEffect, useState } from "react";
import {
    CheckCircle2,
    ExternalLink,
    MessageCircle,
    PauseCircle,
    PlayCircle,
    RefreshCw,
    Save,
    Send,
    Settings2,
    ShieldCheck,
    Zap,
} from "lucide-react";
import { toast } from "sonner";
import { whatsappMerkez } from "@/lib/api";
import WhatsAppMerkezModuller from "./WhatsAppMerkezModuller";
import WhatsAppUltraDashboard from "@/components/whatsapp/WhatsAppUltraDashboard";
import WhatsAppLifecycleSettings from "./WhatsAppLifecycleSettings";
import WhatsAppKurallarPanel from "./WhatsAppKurallarPanel";
import WhatsAppBildirimMerkezi from "./WhatsAppBildirimMerkezi";
import WhatsAppModulMerkezi from "@/components/whatsapp/WhatsAppModulMerkezi";
import YatarOnayPanel from "./YatarOnayPanel";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const TABS = [
    { id: "moduller", label: "Modül Merkezi" },
    { id: "gelen", label: "Gelen kutusu" },
    { id: "bot-akis", label: "Bot akışı" },
    { id: "yatar-onay", label: "Yatar onayları" },
    { id: "tahkim", label: "Tahkim adayları" },
    { id: "evrak", label: "Evrak avcısı" },
    { id: "komuta", label: "Komuta" },
    { id: "ozet", label: "Özet" },
    { id: "kuyruk", label: "Kuyruk" },
    { id: "gecmis", label: "Geçmiş" },
    { id: "kampanya", label: "Kampanya" },
    { id: "istatistik", label: "İstatistik" },
    { id: "bildirimler", label: "Bildirimler" },
    { id: "sablonlar", label: "Şablonlar" },
    { id: "kurallar", label: "Kurallar" },
    { id: "kvkk", label: "KVKK" },
    { id: "ayarlar", label: "Ayarlar" },
];

const DURUM_RENK = {
    BEKLIYOR: "text-amber-300 bg-amber-500/10 border-amber-500/30",
    ONAY_BEKLIYOR: "text-orange-300 bg-orange-500/10 border-orange-500/30",
    GONDERILIYOR: "text-sky-300 bg-sky-500/10 border-sky-500/30",
    GONDERILDI: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    HATA: "text-rose-300 bg-rose-500/10 border-rose-500/30",
    IPTAL: "text-slate-400 bg-slate-500/10 border-slate-500/30",
};

function DurumBadge({ durum, label }) {
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${DURUM_RENK[durum] || DURUM_RENK.BEKLIYOR}`}
        >
            {label || durum}
        </span>
    );
}

export default function WhatsAppMerkezPage() {
    const [tab, setTab] = useState("moduller");
    const [loading, setLoading] = useState(true);
    const [busyKey, setBusyKey] = useState(null);
    const [ozet, setOzet] = useState(null);
    const [gonderimler, setGonderimler] = useState([]);
    const [sablonlar, setSablonlar] = useState([]);
    const [ayarlar, setAyarlar] = useState(null);
    const [filtre, setFiltre] = useState("");
    const [editSablon, setEditSablon] = useState(null);
    const [yeniSablon, setYeniSablon] = useState(null);
    const [tahkimAdaylar, setTahkimAdaylar] = useState([]);
    const [tahkimMeta, setTahkimMeta] = useState({ toplam: 0, mesaj_yok: 0 });
    const [tahkimSelected, setTahkimSelected] = useState([]);
    const [istatistik, setIstatistik] = useState(null);
    const [degiskenler, setDegiskenler] = useState([]);
    const [muvekkilOpts, setMuvekkilOpts] = useState([]);
    const [dosyaOpts, setDosyaOpts] = useState([]);
    const [muvekkilQ, setMuvekkilQ] = useState("");
    const [manuelForm, setManuelForm] = useState({
        muvekkil_id: "",
        sablon_kod: "yeni_muvekkil",
        dosya_id: "",
    });

    const loadOzet = useCallback(async () => {
        const data = await whatsappMerkez.ozet();
        setOzet(data);
        setAyarlar(data.ayarlar);
    }, []);

    const loadGonderimler = useCallback(async (durum) => {
        const params = { limit: 150 };
        if (durum) params.durum = durum;
        const data = await whatsappMerkez.gonderimler(params);
        setGonderimler(data.gonderimler || []);
    }, []);

    const loadSablonlar = useCallback(async () => {
        const data = await whatsappMerkez.sablonlar();
        setSablonlar(data.sablonlar || []);
    }, []);

    const loadTahkimAdaylar = useCallback(async () => {
        const data = await whatsappMerkez.tahkimAdaylari();
        setTahkimAdaylar(data.dosyalar || []);
        setTahkimMeta({
            toplam: data.toplam || 0,
            mesaj_yok: data.mesaj_yok || 0,
            gonderildi: data.gonderildi || 0,
        });
    }, []);

    const loadIstatistik = useCallback(async () => {
        const data = await whatsappMerkez.istatistik();
        setIstatistik(data);
    }, []);

    const loadDegiskenler = useCallback(async () => {
        const data = await whatsappMerkez.degiskenler();
        setDegiskenler(data.degiskenler || []);
    }, []);

    const loadMuvekkilOpts = useCallback(async (q = "") => {
        const data = await whatsappMerkez.lookupMuvekkiller(q);
        setMuvekkilOpts(data.muvekkiller || []);
    }, []);

    const loadDosyaOpts = useCallback(async (muvekkilId, q = "") => {
        const params = {};
        if (muvekkilId) params.muvekkil_id = muvekkilId;
        if (q) params.q = q;
        const data = await whatsappMerkez.lookupDosyalar(params);
        setDosyaOpts(data.dosyalar || []);
    }, []);

    const refreshAll = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([loadOzet(), loadSablonlar(), loadTahkimAdaylar()]);
            if (tab === "istatistik") await loadIstatistik();
            if (tab === "sablonlar") await loadDegiskenler();
            if (tab === "kuyruk") {
                await loadGonderimler();
            } else if (tab === "gecmis") {
                await loadGonderimler(filtre || undefined);
            } else if (tab === "ozet") {
                await loadGonderimler();
            }
        } catch (e) {
            toast.error("Veriler yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, [tab, filtre, loadOzet, loadGonderimler, loadSablonlar, loadTahkimAdaylar, loadIstatistik, loadDegiskenler]);

    useEffect(() => {
        refreshAll();
    }, [tab]);

    useEffect(() => {
        loadMuvekkilOpts(muvekkilQ);
    }, [muvekkilQ, loadMuvekkilOpts]);

    useEffect(() => {
        if (manuelForm.muvekkil_id) {
            loadDosyaOpts(manuelForm.muvekkil_id);
        } else {
            setDosyaOpts([]);
        }
    }, [manuelForm.muvekkil_id, loadDosyaOpts]);

    useEffect(() => {
        if (tab !== "ozet" && tab !== "tahkim") return undefined;
        const t = setInterval(() => loadOzet().catch(() => {}), 60000);
        return () => clearInterval(t);
    }, [loadOzet, tab]);

    async function runBusy(key, fn) {
        if (busyKey) {
            toast.message("İşlem devam ediyor, lütfen bekleyin…");
            return null;
        }
        setBusyKey(key);
        try {
            return await fn();
        } finally {
            setBusyKey(null);
        }
    }

    async function handleAyarKaydet() {
        try {
            const saved = await whatsappMerkez.ayarlarKaydet(ayarlar);
            setAyarlar(saved);
            toast.success("Ayarlar kaydedildi.");
        } catch (e) {
            toast.error("Kayıt başarısız.");
        }
    }

    async function actionGonder(g) {
        await runBusy(`gonder-${g.id}`, async () => {
            try {
                const res = await whatsappMerkez.gonder(g.id);
                if (res.async) {
                    toast.success("Gönderim kuyruğa alındı. 5–15 sn sonra listeyi yenileyin.");
                    setTimeout(() => refreshGonderimList().catch(() => {}), 5000);
                } else if (res.ok) {
                    toast.success(`Gönderildi (${res.kanal || "ok"})`);
                    await refreshGonderimList();
                } else {
                    toast.warning(res.detail || "wa.me ile manuel gönderin.");
                    if (res.wa_me_url) window.open(res.wa_me_url, "_blank");
                }
            } catch (e) {
                const msg =
                    e?.code === "ECONNABORTED"
                        ? "Sunucu yavaş yanıt verdi; gönderim arka planda sürebilir. 10 sn sonra Yenile'ye basın."
                        : e?.response?.data?.detail || "Gönderim isteği tamamlanamadı.";
                toast.error(msg);
            }
        });
    }

    async function actionOnayla(g) {
        await runBusy(`onayla-${g.id}`, async () => {
            try {
                const res = await whatsappMerkez.onayla(g.id);
                if (res.async) {
                    toast.success("Onaylandı. WhatsApp gönderimi arka planda başladı.");
                    setTimeout(() => refreshGonderimList().catch(() => {}), 5000);
                } else {
                    toast.success("Onaylandı.");
                    await refreshGonderimList();
                }
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Onay isteği tamamlanamadı. Tekrar deneyin.");
            }
        });
    }

    async function actionIptal(g) {
        if (!window.confirm("Bu gönderim iptal edilsin mi?")) return;
        await runBusy(`iptal-${g.id}`, async () => {
            try {
                await whatsappMerkez.iptal(g.id);
                toast.success("İptal edildi.");
                await refreshGonderimList();
            } catch (e) {
                toast.error(e?.response?.data?.detail || "İptal edilemedi.");
            }
        });
    }

    async function actionIsaretle(g) {
        await runBusy(`isaretle-${g.id}`, async () => {
            try {
                await whatsappMerkez.isaretle(g.id);
                toast.success("Manuel gönderildi olarak işaretlendi.");
                await refreshGonderimList();
            } catch (e) {
                toast.error(e?.response?.data?.detail || "İşaretlenemedi. Yenile'ye basıp tekrar deneyin.");
            }
        });
    }

    async function handleKuyrukIsle() {
        try {
            const res = await whatsappMerkez.kuyrukIsle(25);
            toast.success(`${res.processed || 0} mesaj işlendi.`);
            refreshAll();
        } catch (e) {
            toast.error("Kuyruk işlenemedi.");
        }
    }

    async function handleSablonKaydet(sablon) {
        try {
            if (sablon.id) {
                await whatsappMerkez.sablonGuncelle(sablon.id, sablon);
            } else {
                await whatsappMerkez.sablonOlustur(sablon);
            }
            setEditSablon(null);
            setYeniSablon(null);
            toast.success("Şablon kaydedildi.");
            loadSablonlar();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Şablon kaydedilemedi.");
        }
    }

    async function handleManuelKuyruk() {
        if (!manuelForm.muvekkil_id) {
            toast.error("Müvekkil seçin.");
            return;
        }
        try {
            const payload = {
                muvekkil_id: Number(manuelForm.muvekkil_id),
                sablon_kod: manuelForm.sablon_kod,
            };
            if (manuelForm.dosya_id) {
                payload.sigorta_dosyasi_id = Number(manuelForm.dosya_id);
            }
            await whatsappMerkez.gonderimOlustur(payload);
            toast.success("Kuyruğa eklendi.");
            setTab("kuyruk");
            refreshAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Eklenemedi.");
        }
    }

    async function handleTahkimSync() {
        try {
            const res = await whatsappMerkez.tahkimSync();
            toast.success(
                `${res.eklendi || 0} dosya kuyruğa eklendi · ${res.atlandi || 0} atlandı`,
            );
            refreshAll();
        } catch (e) {
            toast.error("Senkron başarısız.");
        }
    }

    async function handleTahkimGonder(dosyaId, hemen = true) {
        await runBusy(`tahkim-${dosyaId}`, async () => {
            try {
                const res = await whatsappMerkez.tahkimGonder({
                    dosya_id: dosyaId,
                    hemen_gonder: hemen,
                });
                if (res.async) {
                    toast.success("Gönderim kuyruğa alındı. 10 sn sonra satır güncellenir.");
                    setTimeout(() => loadTahkimAdaylar().catch(() => {}), 10000);
                } else if (res.ok) {
                    toast.success("WhatsApp gönderildi.");
                    await loadTahkimAdaylar();
                } else {
                    toast.warning(res.detail || "wa.me ile gönderin.");
                    if (res.wa_me_url) window.open(res.wa_me_url, "_blank");
                }
            } catch (e) {
                const msg =
                    e?.code === "ECONNABORTED"
                        ? "Sunucu meşgul; gönderim arka planda sürebilir. Yenile'ye basın."
                        : e?.response?.data?.detail || "Gönderim başlatılamadı.";
                toast.error(msg);
            }
        });
    }

    async function handleTahkimTopluGonder() {
        if (tahkimSelected.length === 0) {
            toast.error("En az bir dosya seçin.");
            return;
        }
        try {
            const res = await whatsappMerkez.tahkimGonder({
                dosya_ids: tahkimSelected,
                hemen_gonder: true,
            });
            toast.success(`${res.basarili || 0} / ${tahkimSelected.length} gönderildi.`);
            setTahkimSelected([]);
            refreshAll();
        } catch (e) {
            toast.error("Toplu gönderim hatası.");
        }
    }

    async function handleOtomasyonCalistir() {
        try {
            const res = await whatsappMerkez.otomasyonCalistir();
            toast.success(
                `Otomasyon tamamlandı · onay: ${res.onay_bekleyen || 0} · zincir g12: ${res.zincir?.gun12 || 0}`,
            );
            refreshAll();
        } catch (e) {
            toast.error("Otomasyon çalıştırılamadı.");
        }
    }

    async function handleRaporPdf() {
        try {
            await whatsappMerkez.raporPdfIndir();
            toast.success("PDF indirildi.");
        } catch (e) {
            toast.error("PDF indirilemedi.");
        }
    }

    function toggleTahkimSelect(id) {
        setTahkimSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    }

    function toggleTahkimSelectAll() {
        const pending = tahkimAdaylar.filter((d) => !d.gonderildi).map((d) => d.dosya_id);
        setTahkimSelected((prev) => (prev.length === pending.length ? [] : pending));
    }

    const stats = ozet?.istatistik || {};
    const baglanti = ozet?.baglanti || {};

    return (
        <div className={`p-4 lg:p-6 space-y-5 ${tab === "gelen" ? "wa-inbox-page max-w-none" : "max-w-[1400px]"}`}>
            <header className="relative overflow-hidden rounded-2xl border border-[rgba(201,169,97,0.22)] bg-gradient-to-br from-[#0d2818] via-[#0b1437] to-[#111c4a] p-5 lg:p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-[rgba(37,211,102,0.35)] bg-[rgba(37,211,102,0.12)] text-[#25d366]">
                            <MessageCircle className="h-6 w-6" />
                        </span>
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#25d366] mb-0.5">
                                Otomasyon · Denetim · Kuyruk
                            </p>
                            <h1 className="font-display text-xl lg:text-2xl gold-text">
                                WhatsApp Merkezi
                            </h1>
                            <p className="text-sm text-[#8b93ae] mt-1">
                                Meta Business olmadan wa.me + Evolution API ile kontrollü gönderim
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                        <div
                            className={`rounded-lg border px-3 py-2 text-xs ${
                                baglanti.connected
                                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                                    : "border-amber-500/40 bg-amber-500/10 text-amber-200"
                            }`}
                        >
                            {baglanti.connected ? "● Bağlı" : "○ wa.me modu"}
                            <span className="block text-[10px] opacity-80 mt-0.5">
                                {baglanti.detail || "Evolution API tanımlı değil"}
                            </span>
                        </div>
                        {(ozet?.inbox_okunmamis || 0) > 0 && (
                            <button
                                type="button"
                                onClick={() => setTab("gelen")}
                                className="rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-sky-200"
                            >
                                📥 {ozet.inbox_okunmamis} okunmamış
                            </button>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={refreshAll}
                            disabled={loading}
                            className="border-[rgba(201,169,97,0.3)] text-[#c9a961]"
                        >
                            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                            Yenile
                        </Button>
                    </div>
                </div>
            </header>

            <div className="rounded-lg border border-[#25d366]/25 bg-[#0d2818]/60 px-4 py-3 text-xs text-[#c5cad8] leading-relaxed">
                <strong className="text-[#F3E5AB]">Nasıl kullanılır:</strong> Butona{" "}
                <em>sadece bir kez</em> basın → yeşil “kuyruğa alındı” mesajını görün → 10 sn bekleyin →{" "}
                <strong className="text-[#F3E5AB]">Yenile</strong>. Üst üste tıklamayın (sistem donar).{" "}
                <strong className="text-amber-300">Onay bekliyor</strong> satırında önce kalkan (onayla), sonra uçak (gönder).
                WhatsApp’tan elle gönderdiyseniz yeşil tik ile işaretleyin.
            </div>

            <div className="flex flex-wrap gap-2 border-b border-white/10 pb-2">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                            tab === t.id
                                ? "bg-[rgba(37,211,102,0.15)] text-[#25d366] border border-[rgba(37,211,102,0.35)]"
                                : "text-[#8b93ae] hover:text-white hover:bg-white/5"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "komuta" && (
                <WhatsAppUltraDashboard runBusy={runBusy} busyKey={busyKey} />
            )}

            {tab === "yatar-onay" && (
                <YatarOnayPanel runBusy={runBusy} busyKey={busyKey} />
            )}

            {tab === "tahkim" && (
                <section className="rounded-xl border border-rose-500/20 bg-gradient-to-br from-[#1a1020]/80 to-[#0b1437]/80 p-4 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h2 className="text-sm font-semibold text-[#f5ecd6] flex items-center gap-2">
                                <Zap className="h-4 w-4 text-rose-400" />
                                Tahkime geçmesi gerekenler
                            </h2>
                            <p className="text-xs text-[#8b93ae] mt-1">
                                Başvuru + 15 gün doldu → 16. günde tahkime geçmeli. Eksper raporu WhatsApp mesajı.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <span className="inline-flex items-center rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-1.5 text-sm font-display text-rose-200">
                                {tahkimMeta.toplam} dosya
                            </span>
                            <Button size="sm" variant="outline" onClick={handleTahkimSync} className="text-xs h-8">
                                Tümünü kuyruğa al
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleTahkimTopluGonder}
                                disabled={tahkimSelected.length === 0}
                                className="bg-[#25d366] hover:bg-[#1da851] text-white text-xs h-8"
                            >
                                <Send className="h-3.5 w-3.5 mr-1" />
                                Seçilenlere WhatsApp gönder ({tahkimSelected.length})
                            </Button>
                        </div>
                    </div>

                    <div className="overflow-x-auto rounded-lg border border-white/10">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-[#8b93ae] bg-black/20">
                                    <th className="py-2 px-3 w-10">
                                        <input
                                            type="checkbox"
                                            checked={
                                                tahkimSelected.length > 0 &&
                                                tahkimSelected.length ===
                                                    tahkimAdaylar.filter((d) => !d.gonderildi).length
                                            }
                                            onChange={toggleTahkimSelectAll}
                                        />
                                    </th>
                                    <th className="py-2 px-2">Dosya</th>
                                    <th className="py-2 px-2">Müvekkil</th>
                                    <th className="py-2 px-2">Gün</th>
                                    <th className="py-2 px-2">WhatsApp</th>
                                    <th className="py-2 px-2 text-right">Tetikleyici</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tahkimAdaylar.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="py-8 text-center text-[#8b93ae]">
                                            Tahkim adayı dosya yok.
                                        </td>
                                    </tr>
                                )}
                                {tahkimAdaylar.map((d) => (
                                    <tr
                                        key={d.dosya_id}
                                        className="border-t border-white/5 hover:bg-white/[0.02]"
                                    >
                                        <td className="py-2.5 px-3">
                                            {!d.gonderildi && (
                                                <input
                                                    type="checkbox"
                                                    checked={tahkimSelected.includes(d.dosya_id)}
                                                    onChange={() => toggleTahkimSelect(d.dosya_id)}
                                                />
                                            )}
                                        </td>
                                        <td className="py-2.5 px-2">
                                            <div className="font-semibold text-[#f5ecd6]">{d.dosya_no}</div>
                                            <div className="text-[10px] text-[#8b93ae]">{d.plaka}</div>
                                        </td>
                                        <td className="py-2.5 px-2">
                                            <div className="text-[#eef1fa]">{d.muvekkil_adi}</div>
                                            <div className="text-[10px] text-[#8b93ae] flex flex-wrap gap-1 mt-0.5">
                                                <span>{d.kategori_label || d.dosya_durumu_display}</span>
                                                {d.telefon_eksik && (
                                                    <span className="text-rose-300 border border-rose-500/30 rounded px-1">
                                                        Telefon eksik
                                                    </span>
                                                )}
                                                {d.evrak_tam === false && (
                                                    <span className="text-amber-300 border border-amber-500/30 rounded px-1">
                                                        Evrak eksik
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-2.5 px-2">
                                            <span className="text-rose-300 font-semibold">{d.gecen_gun ?? "—"}</span>
                                            <span className="text-[10px] text-[#8b93ae] block">gün</span>
                                        </td>
                                        <td className="py-2.5 px-2">
                                            <DurumBadge
                                                durum={d.gonderildi ? "GONDERILDI" : d.wa_durum || "BEKLIYOR"}
                                                label={d.wa_durum_display}
                                            />
                                        </td>
                                        <td className="py-2.5 px-2">
                                            <div className="flex justify-end gap-1">
                                                {!d.gonderildi && (
                                                    <Button
                                                        size="sm"
                                                        className="h-7 text-[10px] bg-[#25d366] hover:bg-[#1da851] text-white px-2"
                                                        disabled={!d.telefon_gecerli}
                                                        onClick={() => handleTahkimGonder(d.dosya_id)}
                                                    >
                                                        WhatsApp gönder
                                                    </Button>
                                                )}
                                                {d.wa_me_url && !d.gonderildi && (
                                                    <a
                                                        href={d.wa_me_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex h-7 w-7 items-center justify-center rounded border border-[#25d366]/40 text-[#25d366]"
                                                        title="WhatsApp'ta aç"
                                                    >
                                                        <ExternalLink className="h-3.5 w-3.5" />
                                                    </a>
                                                )}
                                                {d.gonderim_id && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 text-[10px] px-2"
                                                        onClick={() => setTab("kuyruk")}
                                                    >
                                                        Kuyruk
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {tab === "ozet" && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[
                        ["Bekleyen", stats.bekleyen, "BEKLIYOR"],
                        ["Onay", stats.onay, "ONAY_BEKLIYOR"],
                        ["Gönderildi", stats.gonderildi, "GONDERILDI"],
                        ["Hata", stats.hata, "HATA"],
                        ["İptal", stats.iptal, "IPTAL"],
                        ["Toplam", stats.toplam, ""],
                    ].map(([label, val, durum]) => (
                        <button
                            key={label}
                            type="button"
                            onClick={() => {
                                if (durum) {
                                    setFiltre(durum);
                                    setTab(durum === "ONAY_BEKLIYOR" ? "kuyruk" : "gecmis");
                                }
                            }}
                            className="rounded-xl border border-white/10 bg-[#111c4a]/60 p-4 text-left hover:border-[rgba(201,169,97,0.25)] transition"
                        >
                            <p className="text-[10px] uppercase tracking-widest text-[#8b93ae]">{label}</p>
                            <p className="text-2xl font-display text-[#f5ecd6] mt-1">{val ?? 0}</p>
                        </button>
                    ))}
                </div>
            )}

            {(tab === "ozet" || tab === "kuyruk") && (
                <section className="rounded-xl border border-white/10 bg-[#0b1437]/80 p-4 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h2 className="text-sm font-semibold text-[#e6cf94]">
                            {tab === "kuyruk" ? "Onay bekleyen / sırada" : "Hızlı işlemler"}
                        </h2>
                        <Button size="sm" variant="outline" onClick={handleKuyrukIsle} className="text-xs h-8">
                            <PlayCircle className="h-3.5 w-3.5 mr-1" />
                            Kuyruğu işle
                        </Button>
                        {tab === "ozet" && (
                            <>
                                <Button size="sm" variant="outline" onClick={handleOtomasyonCalistir} className="text-xs h-8">
                                    <Zap className="h-3.5 w-3.5 mr-1" />
                                    Günlük otomasyon
                                </Button>
                                <Button size="sm" variant="outline" onClick={handleRaporPdf} className="text-xs h-8">
                                    PDF rapor
                                </Button>
                            </>
                        )}
                    </div>

                    {tab === "ozet" && (
                        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
                            <div className="md:col-span-2">
                                <label className="text-[10px] uppercase tracking-widest text-[#8b93ae] block mb-1">
                                    Müvekkil ara / seç
                                </label>
                                <input
                                    placeholder="İsim veya telefon ara…"
                                    value={muvekkilQ}
                                    onChange={(e) => setMuvekkilQ(e.target.value)}
                                    className="w-full rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm mb-2"
                                />
                                <select
                                    value={manuelForm.muvekkil_id}
                                    onChange={(e) =>
                                        setManuelForm((p) => ({
                                            ...p,
                                            muvekkil_id: e.target.value,
                                            dosya_id: "",
                                        }))
                                    }
                                    className="w-full rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                                >
                                    <option value="">Müvekkil seçin…</option>
                                    {muvekkilOpts.map((m) => (
                                        <option key={m.id} value={m.id}>
                                            {m.label} · {m.telefon || "tel yok"}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-[#8b93ae] block mb-1">
                                    Dosya (opsiyonel)
                                </label>
                                <select
                                    value={manuelForm.dosya_id}
                                    onChange={(e) =>
                                        setManuelForm((p) => ({ ...p, dosya_id: e.target.value }))
                                    }
                                    disabled={!manuelForm.muvekkil_id}
                                    className="w-full rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm mt-6"
                                >
                                    <option value="">Dosya seçin…</option>
                                    {dosyaOpts.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-[#8b93ae] block mb-1">
                                    Şablon
                                </label>
                                <select
                                    value={manuelForm.sablon_kod}
                                    onChange={(e) =>
                                        setManuelForm((p) => ({ ...p, sablon_kod: e.target.value }))
                                    }
                                    className="w-full rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm mt-6"
                                >
                                    {sablonlar.map((s) => (
                                        <option key={s.kod} value={s.kod}>
                                            {s.ad}
                                        </option>
                                    ))}
                                </select>
                                <Button
                                    onClick={handleManuelKuyruk}
                                    className="w-full mt-2 bg-[#25d366] hover:bg-[#1da851] text-white h-10"
                                >
                                    <Send className="h-4 w-4 mr-1.5" />
                                    Kuyruğa ekle
                                </Button>
                            </div>
                        </div>
                    )}

                    <GonderimTable
                        rows={
                            tab === "kuyruk"
                                ? gonderimler.filter((g) =>
                                      ["ONAY_BEKLIYOR", "BEKLIYOR", "HATA"].includes(g.durum),
                                  )
                                : gonderimler.slice(0, 8)
                        }
                        onGonder={actionGonder}
                        onOnayla={actionOnayla}
                        onIptal={actionIptal}
                        onIsaretle={actionIsaretle}
                        compact={tab === "ozet"}
                    />
                </section>
            )}

            {tab === "gecmis" && (
                <section className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                        {["", "GONDERILDI", "HATA", "IPTAL", "BEKLIYOR"].map((d) => (
                            <button
                                key={d || "all"}
                                type="button"
                                onClick={() => {
                                    setFiltre(d);
                                    loadGonderimler(d || undefined);
                                }}
                                className={`px-3 py-1 rounded-full text-xs border ${
                                    filtre === d
                                        ? "border-[#c9a961] text-[#c9a961]"
                                        : "border-white/10 text-[#8b93ae]"
                                }`}
                            >
                                {d || "Tümü"}
                            </button>
                        ))}
                    </div>
                    <GonderimTable
                        rows={gonderimler}
                        onGonder={actionGonder}
                        onOnayla={actionOnayla}
                        onIptal={actionIptal}
                        onIsaretle={actionIsaretle}
                    />
                </section>
            )}

            {tab === "istatistik" && istatistik && (
                <section className="rounded-xl border border-white/10 bg-[#0b1437]/80 p-5 space-y-4">
                    <h2 className="text-sm font-semibold text-[#e6cf94]">Haftalık istatistik</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            ["Gönderildi", istatistik.gonderildi],
                            ["Bekleyen", istatistik.bekleyen],
                            ["Hata", istatistik.hata],
                            ["Başarı %", istatistik.basari_orani],
                        ].map(([label, val]) => (
                            <div
                                key={label}
                                className="rounded-xl border border-white/10 bg-[#111c4a]/60 p-4"
                            >
                                <p className="text-[10px] uppercase tracking-widest text-[#8b93ae]">{label}</p>
                                <p className="text-2xl font-display text-[#f5ecd6] mt-1">{val ?? 0}</p>
                            </div>
                        ))}
                    </div>
                    <p className="text-xs text-[#8b93ae]">
                        Dönem: {istatistik.hafta_baslangic} — {istatistik.hafta_bitis} · Hata oranı %
                        {istatistik.hata_orani}
                    </p>
                    <Button size="sm" variant="outline" onClick={handleRaporPdf}>
                        Haftalık PDF indir
                    </Button>
                </section>
            )}

            {tab === "moduller" && (
                <WhatsAppModulMerkezi runBusy={runBusy} busyKey={busyKey} />
            )}

            {tab === "bildirimler" && (
                <WhatsAppBildirimMerkezi runBusy={runBusy} busyKey={busyKey} />
            )}

            {tab === "sablonlar" && (
                <section className="space-y-4">
                    <div className="flex flex-wrap justify-between gap-2 items-center">
                        <p className="text-sm text-[#8b93ae]">
                            Değişkenler: {"{muvekkil_ad}"}, {"{dosya_no}"}, {"{plaka}"}, {"{panel_url}"}, {"{kusur_link}"}, {"{footer}"}
                        </p>
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                    whatsappMerkez.sablonSeed().then(() => {
                                        loadSablonlar();
                                        toast.success("Varsayılan şablonlar yüklendi.");
                                    })
                                }
                            >
                                Varsayılanları yükle
                            </Button>
                            <Button
                                size="sm"
                                onClick={() =>
                                    setYeniSablon({
                                        kod: "",
                                        ad: "",
                                        mesaj_icerigi: "",
                                        tetikleyici: "MANUEL",
                                        aktif: true,
                                        otomatik_gonder: false,
                                        sira: 99,
                                    })
                                }
                            >
                                Yeni şablon
                            </Button>
                        </div>
                    </div>

                    {(yeniSablon || editSablon) && (
                        <SablonEditor
                            sablon={yeniSablon || editSablon}
                            degiskenler={degiskenler}
                            muvekkilOpts={muvekkilOpts}
                            onChange={yeniSablon ? setYeniSablon : setEditSablon}
                            onSave={() => handleSablonKaydet(yeniSablon || editSablon)}
                            onCancel={() => {
                                setYeniSablon(null);
                                setEditSablon(null);
                            }}
                        />
                    )}

                    <div className="grid gap-3">
                        {sablonlar.map((s) => (
                            <div
                                key={s.id}
                                className="rounded-xl border border-white/10 bg-[#111c4a]/50 p-4"
                            >
                                <div className="flex flex-wrap justify-between gap-2 mb-2">
                                    <div>
                                        <h3 className="font-semibold text-[#f5ecd6]">{s.ad}</h3>
                                        <p className="text-xs text-[#8b93ae]">
                                            {s.kod} · {s.tetikleyici_display}
                                            {s.aktif ? " · Aktif" : " · Pasif"}
                                        </p>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setEditSablon({ ...s })}
                                    >
                                        Düzenle
                                    </Button>
                                </div>
                                <pre className="text-xs text-[#c5cad8] whitespace-pre-wrap max-h-32 overflow-y-auto bg-black/20 rounded-lg p-3">
                                    {s.mesaj_icerigi}
                                </pre>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {tab === "kurallar" && (
                <WhatsAppKurallarPanel busyKey={busyKey} runBusy={runBusy} />
            )}

            {["gelen", "bot-akis", "evrak", "kampanya", "kvkk"].includes(tab) && (
                <WhatsAppMerkezModuller tab={tab} busyKey={busyKey} runBusy={runBusy} />
            )}

            {tab === "ayarlar" && ayarlar && (
                <section className="space-y-6">
                    <WhatsAppLifecycleSettings
                        ayarlar={ayarlar}
                        setAyarlar={setAyarlar}
                        onSaved={loadOzet}
                    />

                    <div className="rounded-xl border border-white/10 bg-[#0b1437]/80 p-5 space-y-4 max-w-4xl">
                    <h2 className="flex items-center gap-2 text-sm font-semibold text-[#e6cf94]">
                        <Settings2 className="h-4 w-4" />
                        Evolution API & bağlantı
                    </h2>
                        <input
                            placeholder="API URL — http://127.0.0.1:8080"
                            value={ayarlar.evolution_api_url || ""}
                            onChange={(e) =>
                                setAyarlar((p) => ({ ...p, evolution_api_url: e.target.value }))
                            }
                            className="w-full rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                        />
                        <input
                            placeholder="API anahtarı"
                            value={ayarlar.evolution_api_key || ""}
                            onChange={(e) =>
                                setAyarlar((p) => ({ ...p, evolution_api_key: e.target.value }))
                            }
                            className="w-full rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                        />
                        <input
                            placeholder="Instance adı — mbs"
                            value={ayarlar.evolution_instance || "mbs"}
                            onChange={(e) =>
                                setAyarlar((p) => ({ ...p, evolution_instance: e.target.value }))
                            }
                            className="w-full rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                        />
                        <input
                            placeholder="Webhook gizli anahtarı (opsiyonel)"
                            value={ayarlar.webhook_secret || ""}
                            onChange={(e) =>
                                setAyarlar((p) => ({ ...p, webhook_secret: e.target.value }))
                            }
                            className="w-full rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                        />
                        <p className="text-[10px] text-[#8b93ae]">
                            Webhook URL: https://sistem.mbshukuk.com/whatsapp/evolution/webhook/
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            <input
                                type="number"
                                placeholder="Dakikada max gönderim"
                                value={ayarlar.rate_limit_dakika ?? 20}
                                onChange={(e) =>
                                    setAyarlar((p) => ({
                                        ...p,
                                        rate_limit_dakika: Number(e.target.value),
                                    }))
                                }
                                className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                            />
                            <input
                                type="number"
                                placeholder="Günlük kampanya limiti"
                                value={ayarlar.kampanya_gunluk_limit ?? 50}
                                onChange={(e) =>
                                    setAyarlar((p) => ({
                                        ...p,
                                        kampanya_gunluk_limit: Number(e.target.value),
                                    }))
                                }
                                className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                            />
                        </div>

                    <Textarea
                        placeholder="Mesaj alt bilgisi (footer)"
                        value={ayarlar.mesaj_footer || ""}
                        onChange={(e) =>
                            setAyarlar((p) => ({ ...p, mesaj_footer: e.target.value }))
                        }
                        rows={2}
                        className="bg-[#111c4a] border-white/10 text-sm"
                    />
                    <input
                        placeholder="Panel URL"
                        value={ayarlar.panel_url || ""}
                        onChange={(e) => setAyarlar((p) => ({ ...p, panel_url: e.target.value }))}
                        className="w-full rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                    />
                    <input
                        placeholder="Test telefon — 905xxxxxxxxx"
                        value={ayarlar.test_telefon || ""}
                        onChange={(e) => setAyarlar((p) => ({ ...p, test_telefon: e.target.value }))}
                        className="w-full rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                    />
                    <div className="rounded-lg border border-white/10 bg-[#0d1533] p-3 space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={ayarlar.mesai_aktif !== false}
                                onChange={(e) =>
                                    setAyarlar((p) => ({ ...p, mesai_aktif: e.target.checked }))
                                }
                            />
                            Otomatik mesajlar yalnızca mesai saatinde (09:30–19:30)
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <input
                                placeholder="Mesai başlangıç (09:30)"
                                value={ayarlar.mesai_baslangic || "09:30"}
                                onChange={(e) =>
                                    setAyarlar((p) => ({ ...p, mesai_baslangic: e.target.value }))
                                }
                                className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                            />
                            <input
                                placeholder="Mesai bitiş (19:30)"
                                value={ayarlar.mesai_bitis || "19:30"}
                                onChange={(e) =>
                                    setAyarlar((p) => ({ ...p, mesai_bitis: e.target.value }))
                                }
                                className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <input
                            type="number"
                            min={0}
                            max={23}
                            placeholder="Cron saat"
                            value={ayarlar.cron_saat ?? 9}
                            onChange={(e) =>
                                setAyarlar((p) => ({ ...p, cron_saat: Number(e.target.value) }))
                            }
                            className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                        />
                        <input
                            type="number"
                            min={1}
                            placeholder="Hatırlatma gün"
                            value={ayarlar.hatirlatma_gun ?? 7}
                            onChange={(e) =>
                                setAyarlar((p) => ({ ...p, hatirlatma_gun: Number(e.target.value) }))
                            }
                            className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                        />
                        <input
                            type="number"
                            min={1}
                            max={5}
                            placeholder="Max hatırlatma"
                            value={ayarlar.hatirlatma_max ?? 2}
                            onChange={(e) =>
                                setAyarlar((p) => ({ ...p, hatirlatma_max: Number(e.target.value) }))
                            }
                            className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                        />
                    </div>

                    <Button onClick={handleAyarKaydet} className="bg-[#25d366] hover:bg-[#1da851] text-white">
                        <Save className="h-4 w-4 mr-1.5" />
                        Ayarları kaydet
                    </Button>
                    </div>
                </section>
            )}
        </div>
    );
}

function GonderimTable({ rows, onGonder, onOnayla, onIptal, onIsaretle, compact }) {
    if (!rows?.length) {
        return <p className="text-sm text-[#8b93ae] py-4 text-center">Kayıt yok.</p>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-[#8b93ae] border-b border-white/10">
                        <th className="py-2 pr-2">Müvekkil</th>
                        <th className="py-2 pr-2">Şablon</th>
                        <th className="py-2 pr-2">Durum</th>
                        {!compact && <th className="py-2 pr-2">Telefon</th>}
                        <th className="py-2 pr-2 text-right">İşlem</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((g) => (
                        <tr key={g.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                            <td className="py-2.5 pr-2">
                                <div className="font-medium text-[#f5ecd6]">{g.muvekkil_adi}</div>
                                {g.dosya_no && (
                                    <div className="text-[10px] text-[#8b93ae]">{g.dosya_no}</div>
                                )}
                            </td>
                            <td className="py-2.5 pr-2 text-xs text-[#c5cad8]">{g.sablon_kod}</td>
                            <td className="py-2.5 pr-2">
                                <DurumBadge durum={g.durum} label={g.durum_display} />
                                {g.delivery_display && g.durum === "GONDERILDI" && (
                                    <div className="text-[10px] text-[#8b93ae] mt-0.5">{g.delivery_display}</div>
                                )}
                            </td>
                            {!compact && (
                                <td className="py-2.5 pr-2 text-xs font-mono text-[#8b93ae]">{g.telefon}</td>
                            )}
                            <td className="py-2.5 pr-2">
                                <div className="flex justify-end flex-wrap gap-1">
                                    {g.durum === "ONAY_BEKLIYOR" && (
                                        <ActionBtn
                                            icon={ShieldCheck}
                                            title="Onayla"
                                            onClick={() => onOnayla(g)}
                                        />
                                    )}
                                    {["BEKLIYOR", "HATA", "ONAY_BEKLIYOR"].includes(g.durum) && (
                                        <ActionBtn icon={Send} title="Gönder" onClick={() => onGonder(g)} />
                                    )}
                                    {g.wa_me_url && (
                                        <a
                                            href={g.wa_me_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex h-7 w-7 items-center justify-center rounded border border-[#25d366]/40 text-[#25d366] hover:bg-[#25d366]/10"
                                            title="WhatsApp'ta aç"
                                        >
                                            <ExternalLink className="h-3.5 w-3.5" />
                                        </a>
                                    )}
                                    {g.durum !== "GONDERILDI" && g.durum !== "IPTAL" && (
                                        <>
                                            <ActionBtn
                                                icon={CheckCircle2}
                                                title="Gönderildi işaretle"
                                                onClick={() => onIsaretle(g)}
                                            />
                                            <ActionBtn
                                                icon={PauseCircle}
                                                title="İptal"
                                                onClick={() => onIptal(g)}
                                            />
                                        </>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ActionBtn({ icon: Icon, title, onClick }) {
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-white/10 text-[#c9a961] hover:bg-white/5"
        >
            <Icon className="h-3.5 w-3.5" />
        </button>
    );
}

function SablonEditor({ sablon, onChange, onSave, onCancel, degiskenler = [], muvekkilOpts = [] }) {
    const [onizleme, setOnizleme] = useState("");
    const [onizlemeMuvekkil, setOnizlemeMuvekkil] = useState("");

    useEffect(() => {
        if (!onizlemeMuvekkil || !sablon.mesaj_icerigi) {
            setOnizleme("");
            return;
        }
        whatsappMerkez
            .onizleme({
                muvekkil_id: Number(onizlemeMuvekkil),
                mesaj_icerigi: sablon.mesaj_icerigi,
            })
            .then((d) => setOnizleme(d.mesaj || ""))
            .catch(() => setOnizleme(""));
    }, [onizlemeMuvekkil, sablon.mesaj_icerigi]);

    function insertVar(name) {
        onChange({ ...sablon, mesaj_icerigi: `${sablon.mesaj_icerigi || ""}{${name}}` });
    }

    async function handleTestGonder() {
        try {
            const res = await whatsappMerkez.testGonder({
                sablon_kod: sablon.kod,
                muvekkil_id: onizlemeMuvekkil ? Number(onizlemeMuvekkil) : undefined,
            });
            if (res.ok) toast.success(`Test gönderildi (${res.kanal})`);
            else {
                toast.warning(res.detail || "Gönderilemedi");
                if (res.wa_me_url) window.open(res.wa_me_url, "_blank");
            }
        } catch (e) {
            toast.error("Test gönderimi başarısız.");
        }
    }

    return (
        <div className="rounded-xl border border-[#25d366]/30 bg-[#0d2818]/30 p-4 space-y-3">
            <div className="grid lg:grid-cols-2 gap-4">
                <div className="space-y-3">
                    <div className="grid md:grid-cols-2 gap-2">
                        <input
                            placeholder="Kod (yeni_muvekkil)"
                            value={sablon.kod}
                            disabled={!!sablon.id}
                            onChange={(e) => onChange({ ...sablon, kod: e.target.value })}
                            className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                        />
                        <input
                            placeholder="Görünen ad"
                            value={sablon.ad}
                            onChange={(e) => onChange({ ...sablon, ad: e.target.value })}
                            className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                        />
                    </div>
                    <select
                        value={sablon.tetikleyici}
                        onChange={(e) => onChange({ ...sablon, tetikleyici: e.target.value })}
                        className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm w-full"
                    >
                        <option value="MANUEL">Yalnızca manuel</option>
                        <option value="YENI_MUVEKKIL">Yeni müvekkil</option>
                        <option value="TAHKIM_GECIS">Tahkime geçiş</option>
                        <option value="TAHKIM_GUN12">Tahkim uyarı — gün 12</option>
                        <option value="TAHKIM_GUN15">Tahkim uyarı — gün 15</option>
                        <option value="NONE">Kapalı</option>
                    </select>
                    <div className="flex flex-wrap gap-1">
                        {(degiskenler.length ? degiskenler : [
                            "muvekkil_ad", "dosya_no", "plaka", "kalan_gun", "panel_url", "kusur_link", "footer",
                        ]).map((v) => (
                            <button
                                key={v}
                                type="button"
                                onClick={() => insertVar(v)}
                                className="text-[10px] px-2 py-0.5 rounded-full border border-[#25d366]/40 text-[#25d366] hover:bg-[#25d366]/10"
                            >
                                {`{${v}}`}
                            </button>
                        ))}
                    </div>
                    <Textarea
                        value={sablon.mesaj_icerigi}
                        onChange={(e) => onChange({ ...sablon, mesaj_icerigi: e.target.value })}
                        rows={12}
                        className="bg-[#111c4a] border-white/10 font-mono text-xs"
                    />
                    <select
                        value={onizlemeMuvekkil}
                        onChange={(e) => setOnizlemeMuvekkil(e.target.value)}
                        className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm w-full"
                    >
                        <option value="">Önizleme müvekkili…</option>
                        {muvekkilOpts.map((m) => (
                            <option key={m.id} value={m.id}>
                                {m.label}
                            </option>
                        ))}
                    </select>
                    <div className="flex gap-2 flex-wrap">
                        <Button onClick={onSave} className="bg-[#25d366] text-white">
                            Kaydet
                        </Button>
                        <Button variant="outline" onClick={onCancel}>
                            İptal
                        </Button>
                        <Button variant="outline" onClick={handleTestGonder} disabled={!sablon.kod}>
                            Test gönder
                        </Button>
                    </div>
                </div>
                <div className="flex justify-center">
                    <div className="w-[280px] rounded-[2rem] border-4 border-[#1a1a1a] bg-[#111] p-3 shadow-xl">
                        <div className="rounded-2xl bg-[#0b141a] min-h-[420px] overflow-hidden">
                            <div className="bg-[#1f2c34] px-3 py-2 text-[10px] text-[#25d366]">
                                WhatsApp · Önizleme
                            </div>
                            <div className="p-3">
                                <div className="rounded-lg bg-[#005c4b] p-3 text-xs text-white whitespace-pre-wrap max-h-[360px] overflow-y-auto">
                                    {onizleme || sablon.mesaj_icerigi || "Mesaj önizlemesi…"}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
